import * as React from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router-dom';
import { getDevBoxViewModels } from '../../components/dev-box/selectors';
import { getEnvironmentViewModels } from '../../components/environment/selectors';
import { FeatureFlagName } from '../../constants/features';
import { useActionCreator } from '../../hooks/action-creator';
import { useFeatureFlag } from '../../hooks/features';
import HomeIndexPage from '../../pages/home/index';
import LoadingPage from '../../pages/loading';
import { getVisibleDevBoxes, getVisibleEnvironments } from '../../pages/home/resources';
import { initializeHome } from '../../redux/actions/sub-applications/home/home-action-creators';
import { getHasDevBoxes } from '../../redux/selector/dev-box-selectors';
import { getHasEnvironments } from '../../redux/selector/environment-selectors';
import { getDisplayState } from '../../redux/selector/display/display-state';
import { getIsHomeInitialized } from '../../redux/selector/sub-applications/home-selectors';
import { getProjectFilterFromCurrentLocation, getResourceFilterFromCurrentLocation } from '../../utilities/filters';
import { HomeLoadPerformanceTelemetry } from './home-load-performance-telemetry';
import { getIsLoading } from './selectors';

export const getShouldRenderHomeShell = (isProgressiveHomeLoadingEnabled: boolean, isLoading: boolean): boolean =>
    isProgressiveHomeLoadingEnabled || !isLoading;

export const HomeSubApplication: React.FC = () => {
    // Application state hooks
    const isInitialized = useSelector(getIsHomeInitialized);
    const isLoading = useSelector(getIsLoading);
    const displayState = useSelector(getDisplayState);
    const devBoxes = useSelector(getDevBoxViewModels);
    const environments = useSelector(getEnvironmentViewModels);
    const hasDevBoxes = useSelector(getHasDevBoxes);
    const hasEnvironments = useSelector(getHasEnvironments);
    const isProgressiveHomeLoadingEnabled = useFeatureFlag(FeatureFlagName.EnableProgressiveHomeLoading);

    // Filter providers are rendered below this component, so capture the same initial values directly from the URL.
    const resourceFilter = React.useMemo(getResourceFilterFromCurrentLocation, []);
    const projectFilter = React.useMemo(getProjectFilterFromCurrentLocation, []);

    const visibleDevBoxes = React.useMemo(
        () => getVisibleDevBoxes(hasDevBoxes, devBoxes, resourceFilter, projectFilter),
        [devBoxes, hasDevBoxes, projectFilter, resourceFilter]
    );
    const visibleEnvironments = React.useMemo(
        () => getVisibleEnvironments(hasEnvironments, environments, resourceFilter, projectFilter),
        [environments, hasEnvironments, projectFilter, resourceFilter]
    );

    // Action hooks
    const initialize = useActionCreator(initializeHome);

    // Effect hooks
    React.useEffect(() => {
        if (!isInitialized) {
            initialize();
        }
    }, [isInitialized]);

    const shouldRenderHomeShell = getShouldRenderHomeShell(isProgressiveHomeLoadingEnabled, isLoading);

    return (
        <>
            <HomeLoadPerformanceTelemetry
                devBoxCount={visibleDevBoxes.length}
                displayState={displayState}
                environmentCount={visibleEnvironments.length}
                isHomeContentRendered={!isLoading}
                isHomeShellRendered={shouldRenderHomeShell}
            />
            {!shouldRenderHomeShell ? (
                <Routes>
                    <Route path="*" element={<LoadingPage />} />
                </Routes>
            ) : (
                <Routes>
                    <Route path="*" element={<HomeIndexPage />} />
                </Routes>
            )}
        </>
    );
};

export default HomeSubApplication;
